import { BatchSyncScheduledChargesResponse } from '@common/entities';
import { get, post } from 'config/api/config';

const SCHEDULED_CHARGES_ENDPOINTS = '/viewer/scheduled-charges';

interface ScheduleChargeResponse {
  orgId: number;
  leaseId: number;
  propertyId: number;
  leaseIntervalId: number;
  arTriggerId: number;
  arTriggerName: string;
  chargeStartDate: string;
  chargeEndDate: string;
  installmentId: number;
  endsWithMoveOut: boolean;
  chargeAmount: {
    amountNumber: number;
    amountFormatted: string;
    currencyCode: string;
    isRange?: boolean;
    minAmountNumber?: number;
    maxAmountNumber?: number;
    minAmountFormatted?: string;
    maxAmountFormatted?: string;
  };
  totalValue: string;
  deletedOn: string;
  ledgerFilterId: number;
  arCodeName: string;
  isHistorical: number;
  customerName: string;
  taxAmount: string;
  summarizationGroupId: number | string;
}

export const getScheduledCharges = async (
  propertyId: number,
  leaseId: number,
  leaseIntervalId?: number,
) => {
  if (!propertyId || !leaseId) throw new Error('Missing parameter.');

  const response = await get<ScheduleChargeResponse[]>(
    `${SCHEDULED_CHARGES_ENDPOINTS}?propertyId=${propertyId}&leaseId=${leaseId}&leaseIntervalId=${leaseIntervalId}`,
  );
  return response;
};

interface SyncScheduledChargesItem {
  applicationResponseId: string;
  propertyId: number;
  leaseId: number;
  leaseIntervalId?: number;
}

export const syncScheduledCharges = async (
  propertyId: number,
  leaseId: number,
  applicationResponseId: string,
  leaseIntervalId?: number,
  signal?: AbortSignal,
) => {
  if (!propertyId || !leaseId) throw new Error('Missing propertyId or leaseId.');
  if (!applicationResponseId) throw new Error('Missing applicationResponseId.');

  // Single application sync (backwards compatible for CartSection)
  const response = await post<BatchSyncScheduledChargesResponse>(
    `${SCHEDULED_CHARGES_ENDPOINTS}/sync`,
    {
      applications: [
        {
          applicationResponseId,
          propertyId,
          leaseId,
          leaseIntervalId,
        },
      ],
    },
    signal ? { signal } : undefined,
  );

  // Return the batch response (already has charges and notifications)
  return response;
};

export const batchSyncScheduledCharges = async (applications: SyncScheduledChargesItem[]) => {
  if (!applications || applications.length === 0) {
    throw new Error('No applications provided');
  }

  const response = await post<BatchSyncScheduledChargesResponse>(
    `${SCHEDULED_CHARGES_ENDPOINTS}/sync`,
    { applications },
  );

  return response;
};
